import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, UserPlus, X, Crown, Eye, Pencil } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";

const MembersDialog = ({ open, onOpenChange, boardId, isOwner, onChanged }) => {
  const [board, setBoard] = useState(null);
  const [directory, setDirectory] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [inviteRole, setInviteRole] = useState("editor");

  const load = async () => {
    if (!boardId) return;
    setLoading(true);
    try {
      const [bd, dr] = await Promise.all([
        api.get(`/boards/${boardId}`),
        api.get("/users/directory"),
      ]);
      setBoard(bd.data);
      setDirectory(dr.data);
    } catch (e) { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, boardId]);

  const invite = async (u) => {
    try {
      await api.post(`/boards/${boardId}/members`, { user_id: u.user_id, role: inviteRole });
      toast.success(`${u.name || u.email} invitato come ${inviteRole === "viewer" ? "sola lettura" : "editor"}`);
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const changeRole = async (u, role) => {
    try {
      await api.put(`/boards/${boardId}/members/${u.user_id}`, { user_id: u.user_id, role });
      toast.success(`${u.name || u.email}: ora ${role === "viewer" ? "sola lettura" : "editor"}`);
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const remove = async (u) => {
    try {
      await api.delete(`/boards/${boardId}/members/${u.user_id}`);
      toast.success(`${u.name || u.email} rimosso`);
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const memberIds = new Set(board?.member_ids || []);
  const ownerId = board?.owner_id;
  const memberRoles = board?.member_roles || {};
  const candidates = directory.filter((u) => u.user_id !== ownerId && !memberIds.has(u.user_id))
    .filter((u) => !q || (u.name || "").toLowerCase().includes(q.toLowerCase()) || (u.email || "").toLowerCase().includes(q.toLowerCase()));

  const Avatar = ({ u, size = 8 }) => u.picture ? (
    <img src={u.picture} alt="" className={`w-${size} h-${size} rounded-full object-cover`} />
  ) : (
    <div className={`w-${size} h-${size} rounded-full bg-slate-200 text-slate-700 flex items-center justify-center text-xs font-semibold`}>
      {(u.name || u.email)[0]?.toUpperCase()}
    </div>
  );

  const RoleBadge = ({ role }) => role === "viewer" ? (
    <Badge className="rounded-full bg-slate-200 text-slate-700 text-[9px] uppercase tracking-widest border-0">
      <Eye className="w-2.5 h-2.5 mr-1" /> Sola lettura
    </Badge>
  ) : (
    <Badge className="rounded-full bg-emerald-100 text-emerald-800 text-[9px] uppercase tracking-widest border-0">
      <Pencil className="w-2.5 h-2.5 mr-1" /> Editor
    </Badge>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-2xl bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-2xl tracking-tight">Collaboratori</DialogTitle>
        </DialogHeader>

        {loading || !board ? (
          <div className="text-sm text-slate-500 py-6">Caricamento…</div>
        ) : (
          <div className="space-y-5">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Membri attuali</div>
              <div className="space-y-2">
                {board.owner && (
                  <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-50">
                    <div className="flex items-center gap-2.5">
                      <Avatar u={board.owner} />
                      <div>
                        <div className="text-sm font-medium">{board.owner.name || board.owner.email}</div>
                        <div className="text-[11px] text-slate-500">{board.owner.email}</div>
                      </div>
                    </div>
                    <Badge className="rounded-full bg-[#12466F] text-white text-[9px] uppercase tracking-widest border-0">
                      <Crown className="w-2.5 h-2.5 mr-1" /> Proprietario
                    </Badge>
                  </div>
                )}
                {(board.members || []).map((m) => {
                  const currentRole = memberRoles[m.user_id] || "editor";
                  return (
                    <div key={m.user_id} className="flex items-center justify-between p-2.5 rounded-xl bg-slate-50" data-testid={`member-${m.user_id}`}>
                      <div className="flex items-center gap-2.5 min-w-0">
                        <Avatar u={m} />
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{m.name || m.email}</div>
                          <div className="text-[11px] text-slate-500 truncate">{m.email}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isOwner ? (
                          <Select value={currentRole} onValueChange={(v) => changeRole(m, v)}>
                            <SelectTrigger className="h-7 w-32 rounded-full bg-white border border-slate-200 text-xs" data-testid={`role-select-${m.user_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="editor">
                                <div className="flex items-center gap-1.5"><Pencil className="w-3 h-3" /> Editor</div>
                              </SelectItem>
                              <SelectItem value="viewer">
                                <div className="flex items-center gap-1.5"><Eye className="w-3 h-3" /> Sola lettura</div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <RoleBadge role={currentRole} />
                        )}
                        {isOwner && (
                          <Button size="sm" variant="ghost" className="rounded-full h-7 w-7 p-0 text-rose-600 hover:bg-rose-50" onClick={() => remove(m)} data-testid={`remove-member-${m.user_id}`}>
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
                {(board.members || []).length === 0 && !isOwner && (
                  <div className="text-xs text-slate-400 italic">Nessun collaboratore.</div>
                )}
              </div>
            </div>

            {isOwner && (
              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Aggiungi collaboratori</div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="relative flex-1">
                    <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      data-testid="member-picker-search"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Cerca per nome o email…"
                      className="rounded-xl h-9 pl-8 bg-slate-100 border-0 focus-visible:ring-2 focus-visible:ring-[#007AFF]"
                    />
                  </div>
                  <Select value={inviteRole} onValueChange={setInviteRole}>
                    <SelectTrigger className="w-36 h-9 rounded-xl bg-slate-100 border-0 text-xs" data-testid="invite-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="editor">
                        <div className="flex items-center gap-1.5"><Pencil className="w-3 h-3" /> Editor</div>
                      </SelectItem>
                      <SelectItem value="viewer">
                        <div className="flex items-center gap-1.5"><Eye className="w-3 h-3" /> Sola lettura</div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-[10px] text-slate-500 mb-2">
                  <b>Editor</b>: può aggiungere, modificare, importare, geocodificare ed eliminare immobili. <b>Sola lettura</b>: può visualizzare filtri, mappa e statistiche ma non può modificare nulla.
                </p>
                <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                  {candidates.slice(0, 30).map((u) => (
                    <div key={u.user_id} className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <Avatar u={u} />
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{u.name || u.email}</div>
                          <div className="text-[11px] text-slate-500 truncate">{u.email}</div>
                        </div>
                      </div>
                      <Button size="sm" onClick={() => invite(u)} className="rounded-full h-7 bg-[#007AFF] hover:bg-[#0060df] text-white" data-testid={`invite-${u.user_id}`}>
                        <UserPlus className="w-3 h-3 mr-1" /> Invita
                      </Button>
                    </div>
                  ))}
                  {candidates.length === 0 && (
                    <div className="text-xs text-slate-400 italic py-2 text-center">Nessun utente disponibile.</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MembersDialog;
